import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useDocumentContext } from '../contexts/DocumentContext';
import { UserDetails } from 'types/User';

interface UseDocumentManagementViewProps {
  userDetails: UserDetails | null;
  userReady?: boolean;
  onLogout: () => void;
}

/**
 * Custom hook to manage DocumentManagement view state and sidebar configuration
 * Extracts view switching, sidebar setup, and reminder handling logic
 */
export const useDocumentManagementView = ({
  userDetails,
  userReady,
  onLogout
}: UseDocumentManagementViewProps) => {
  const navigate = useNavigate();
  const { activeItem, setItems, setFooterItems, setActiveItem } = useSidebar();
  const {
    setSelectedUser,
    setExpandedCategoryId,
    setExpandedSubcategoryId,
    setHighlightedDocumentId,
    setHighlightedFieldId
  } = useDocumentContext();

  const [currentView, setCurrentView] = useState('overview');

  // Create sidebar items - all authenticated users can access company docs
  // Visibility of individual documents/fields is controlled by viewRole/editRole
  const sidebarItems = useMemo(() => [
    { id: 'overview', label: 'Overview', icon: '🪟' },
    { id: 'docs', label: 'User Documents', icon: '📑' },
    { id: 'company-docs', label: 'Company Documents', icon: '🏢' },
  ], []);

  const sidebarFooterItems = useMemo(() => [
    { id: 'help', label: 'Help', to: '/ContactUs', icon: '⁉️' },
    { id: 'privacy', label: 'Privacy Policy', to: '/PrivacyPolicy', icon: '⛓️‍💥' },
    { id: 'logout', label: 'Logout', onClick: () => { onLogout() }, icon: '💤' },
  ], [onLogout]);

  const navAndHighlightNavigate = useCallback((userID: UserDetails, documentType: any, documentField: any) => {
    setActiveItem('docs');
    setCurrentView('docs');
    setSelectedUser(userID);

    if (documentType && documentType.id) {
      const subCategoryID = documentType.subcategory_id;
      const categoryId = documentType.category_id;
      setHighlightedDocumentId(documentType.id);

      if (subCategoryID) {
        setExpandedCategoryId(categoryId);
        setExpandedSubcategoryId(subCategoryID);
      } else if (categoryId) {
        setExpandedCategoryId(categoryId);
        setExpandedSubcategoryId(null);
      }
    } else if (documentField && documentField.id) {
      const subCategoryID = documentField.subcategory_id;
      const categoryId = documentField.category_id;
      setHighlightedFieldId && setHighlightedFieldId(documentField.id);

      if (subCategoryID) {
        setExpandedCategoryId(categoryId);
        setExpandedSubcategoryId(subCategoryID);
      } else if (categoryId) {
        setExpandedCategoryId(categoryId);
        setExpandedSubcategoryId(null);
      }
    }
  }, [
    setActiveItem,
    setSelectedUser,
    setHighlightedDocumentId,
    setHighlightedFieldId,
    setExpandedCategoryId,
    setExpandedSubcategoryId,
  ]);

  // Handle reminder click - navigates to docs and highlights relevant section
  const handleReminderClick = useCallback((reminder: any) => {
    navAndHighlightNavigate(reminder.userID, reminder.documentTypeID || {}, reminder.fieldTypeID || {});
  }, [navAndHighlightNavigate]);

  // Initialize sidebar on mount — useLayoutEffect ensures items are present before first paint
  useLayoutEffect(() => {
    setItems(sidebarItems);
    setFooterItems(sidebarFooterItems);
    setActiveItem('overview');
  }, [setItems, setFooterItems, setActiveItem, sidebarItems, sidebarFooterItems]);

  // Sync view with active sidebar item
  useEffect(() => {
    if (activeItem === 'overview' || activeItem === 'docs' || activeItem === 'company-docs' || activeItem === 'reminders') {
      setCurrentView(activeItem);
    } else {
      setCurrentView('overview');
    }
  }, [activeItem]);

  // Clear highlights when switching away from docs view
  useEffect(() => {
    if (currentView !== 'docs') {
      setHighlightedDocumentId(null);
      setHighlightedFieldId && setHighlightedFieldId(null);
      setExpandedCategoryId(null);
      setExpandedSubcategoryId(null);
    }
  }, [currentView, setHighlightedDocumentId, setHighlightedFieldId, setExpandedCategoryId, setExpandedSubcategoryId]);

  // Validation and navigation logic
  useEffect(() => {
    // Redirect to login if no user
    if (!userDetails && userReady !== false) {
      navigate('/LoginRegister', { replace: true });
      return;
    }

    // Redirect to Account if no agency selected
    if (userReady && userDetails && !userDetails.agency) {
      alert('Please select an active agency before managing documents.');
      navigate('/Account');
    }
  }, [userReady, userDetails, navigate]);

  return {
    currentView,
    handleReminderClick,
    navAndHighlightNavigate
  };
};
